/**
 * The one money formatter for apps/web. Lives in lib/ (not in
 * components/financials/shared, whose barrel financials.test.tsx replaces
 * wholesale).
 *
 * Pass the currency of the record being shown (invoice, contract, rate,
 * compensation, payroll config). Values that have no currency of their own —
 * aggregates and time-entry amounts — use the org reporting currency from
 * `useOrgCurrency()`. Scrambl's own subscription pricing stays USD on purpose
 * and does not need this helper.
 */

export const DEFAULT_CURRENCY = 'USD'

const CURRENCY_CODE = /^[A-Z]{3}$/

/** An ISO 4217 code ("usd" → "USD"), or `fallback` when missing/malformed. */
export function normalizeCurrencyCode(
  code: string | null | undefined,
  fallback: string = DEFAULT_CURRENCY,
): string {
  const upper = typeof code === 'string' ? code.trim().toUpperCase() : ''
  return CURRENCY_CODE.test(upper) ? upper : fallback
}

export interface CurrencyFormatOptions {
  /**
   * Show minor units (cents). Uses the currency's own precision, so JPY
   * still shows none. Without it amounts round to whole units.
   */
  cents?: boolean
  /** Compact notation for tight cells and chart axes: "$1.2M", "$12K". */
  compact?: boolean
  /** Unit suffix for rates, e.g. 'hr' renders "$150.00/hr". */
  per?: string
  /** What to show for null/undefined/non-numeric values. */
  empty?: string
}

const formatterCache = new Map<string, Intl.NumberFormat>()

function getFormatter(
  currency: string,
  cents: boolean,
  compact: boolean,
): Intl.NumberFormat {
  const key = `${currency}|${cents ? 1 : 0}|${compact ? 1 : 0}`
  let formatter = formatterCache.get(key)
  if (!formatter) {
    const options: Intl.NumberFormatOptions = { style: 'currency', currency }
    if (compact) {
      options.notation = 'compact'
      // USD's default minimum of 2 fraction digits, capped at 1, would force
      // "$413.0K"; compact money drops trailing zeros ("$413K", "$1.3M").
      options.minimumFractionDigits = 0
      options.maximumFractionDigits = 1
    } else if (!cents) {
      options.minimumFractionDigits = 0
      options.maximumFractionDigits = 0
    }
    formatter = new Intl.NumberFormat('en-US', options)
    formatterCache.set(key, formatter)
  }
  return formatter
}

function toAmount(value: unknown): number | null {
  if (value == null || value === '') return null
  const n = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(n) ? n : null
}

/**
 * Format a money amount: thousands separators, a leading minus ("-$50.00",
 * never "$-50.00") and the right symbol for the currency.
 */
export function formatCurrencyAmount(
  value: number | string | null | undefined,
  currency: string | null | undefined = DEFAULT_CURRENCY,
  options: CurrencyFormatOptions = {},
): string {
  const amount = toAmount(value)
  if (amount == null) return options.empty ?? '—'
  const code = normalizeCurrencyCode(currency)
  const cents = options.cents ?? false
  const compact = options.compact ?? false
  let text: string
  try {
    text = getFormatter(code, cents, compact).format(amount)
  } catch {
    // A well-formed code the runtime doesn't know: show the code itself
    // rather than borrowing a "$".
    const digits = cents && !compact ? 2 : 0
    const number = new Intl.NumberFormat('en-US', {
      notation: compact ? 'compact' : 'standard',
      minimumFractionDigits: digits,
      maximumFractionDigits: compact ? 1 : digits,
    }).format(Math.abs(amount))
    text = `${amount < 0 ? '-' : ''}${code} ${number}`
  }
  // Avoid "-$0" for amounts that round to zero.
  if (text.startsWith('-') && !/[1-9]/.test(text)) text = text.slice(1)
  return options.per ? `${text}/${options.per}` : text
}

interface RateSnapshotCurrencies {
  resolved?: {
    currency?: string | null
    billCurrency?: string | null
    payCurrency?: string | null
    costCurrency?: string | null
  } | null
}

/**
 * The currency a time entry's stored rate snapshot resolved for a bill, pay
 * or cost rate, or null when it is unknown (no snapshot, or a blended rate
 * whose bill currency was unknown) — callers then use the org currency.
 * Legacy snapshots without the per-kind field fall back to `resolved.currency`.
 */
export function rateSnapshotCurrency(
  snapshot: RateSnapshotCurrencies | null | undefined,
  kind: 'bill' | 'pay' | 'cost',
): string | null {
  const resolved = snapshot?.resolved
  if (!resolved) return null
  const key = `${kind}Currency` as const
  const own = resolved[key]
  if (own !== undefined) return own ? normalizeCurrencyCode(own) : null
  return resolved.currency ? normalizeCurrencyCode(resolved.currency) : null
}

interface RateCurrencies {
  currency?: string | null
  billCurrency?: string | null
  payCurrency?: string | null
  costCurrency?: string | null
}

/**
 * The currency of a member rate, rate card entry or effective rate for one
 * kind of rate: the resolved per-kind currency (blended bill currency, the
 * pay/cost source's currency) when present, else the rate's own `currency`,
 * else `fallback` (pass the org currency).
 */
export function rateCurrency(
  rate: RateCurrencies | null | undefined,
  kind: 'bill' | 'pay' | 'cost',
  fallback: string = DEFAULT_CURRENCY,
): string {
  const own = rate?.[`${kind}Currency` as const]
  return normalizeCurrencyCode(own || rate?.currency, fallback)
}
